import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip, TooltipProps, Typography, styled } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    // GridSortModel,
} from '@mui/x-data-grid-premium';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchShipperHealthLanes } from '../../state/features/shipperHealthSlice';
import { Lane } from '../../interfaces/services/lanes';
import { ApiStatus } from '../../utils/enums';
import { formatNumber, formatCurrency } from '../../utils/formattingUtils';
import { buildLaneLink } from '../../utils/navigationHelpers';
import { formatHealthFilters, getLaneId } from '../../utils/stateHelpers';
import { useFilters } from '../Filters/useFilters';

const classesPrefix = 'shipperHealthLanesTable';

const classes = {
    table: `${classesPrefix}-table`,
    tableRow: `${classesPrefix}-tableRow`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => {
    return {
        [`&.${classes.table}`]: {
            height: '300px',
        },
        [`& .${classes.tableRow}`]: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    };
}) as typeof DataGridPremium;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const ShipperHealthLanesTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const routerParams = useParams<{ id: string; }>();
    const filters = useFilters();

    // TODO: TCSC-52750
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);

    const shipperHealthLanesApiStatus = useAppSelector((state) => { return state.shipperHealth.shipperHealthLanesApiStatus; });
    // const shipperHealthLanesApiParams = useAppSelector((state) => { return state.shipperHealth.shipperHealthLanesApiParams; });
    const shipperHealthLanes = useAppSelector((state) => { return state.shipperHealth.shipperHealthLanes; });

    useEffect(() => {
        if (routerParams.id) {
            dispatch(fetchShipperHealthLanes({
                shipperId: routerParams.id,
                // TODO: TCSC-52750
                // ...shipperHealthLanesApiParams,
            }));
        }
    }, [dispatch, routerParams.id]);

    const columns = useMemo((): GridColDef<Lane>[] => {
        return [
            {
                headerName: 'Origin',
                field: 'pickup_service_area_name',
                flex: 1,
            },
            {
                headerName: 'Destination',
                field: 'delivery_service_area_name',
                flex: 1,
            },
            {
                headerName: 'Volume',
                field: 'freight_order_count',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>Volume</span>
                                    <span> is the count of orders for the lane in the past 30 days.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
            },
            {
                headerName: 'Linehaul Rate',
                field: 'linehaul_rate_usd_amount',
                type: 'number',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
            },
            {
                headerName: 'Fuel Rate',
                field: 'average_fuel_surcharge_usd_per_mile_amount',
                type: 'number',
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.fuel_surcharge_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.fuel_surcharge_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
            },
            {
                headerName: 'LOH',
                description: 'Average Length of Haul',
                field: 'average_loaded_distance_miles_amount',
                type: 'number',
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.freight_order_count ?
                        row.loaded_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
            },
            {
                headerName: 'Loading',
                field: 'average_load_hours_duration',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>Load Time</span>
                                    <span> is the number of hours spent at a pickup location.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.load_hours_duration !== null && row.freight_order_count ?
                        row.load_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Scheduled MPH',
                description: 'Scheduled Miles Per Hour',
                field: 'average_scheduled_miles_per_hour',
                flex: 0.5,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>Scheduled Miles Per Hour</span>
                                    <span> is the average expected velocity needed to deliver a load given the early pickup time to the early delivery time.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Actual MPH',
                description: 'Actual Miles Per Hour',
                field: 'average_actual_miles_per_hour',
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>Actual Miles Per Hour</span>
                                    <span> is the average velocity of orders from departing the pickup location to arriving at the delivery location.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'Scheduled MPH vs. Actual MPH',
                description: 'Scheduled Miles Per Hour vs. Actual Miles Per Hour',
                field: 'average_scheduled_vs_actual_miles_per_hour',
                flex: 1,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>Scheduled vs Actual Miles Per Hour</span>
                                    <span> evaluates the difference between Scheduled MPH and Actual MPH where a quotient of 1 is presumed to be delivering on time.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
            {
                headerName: 'In Transit Delay',
                field: 'average_netwise_in_transit_delay_hours_duration',
                flex: 0.5,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip
                            title={
                                <Typography variant='body2' className={classes.helpText}>
                                    <span className={classes.title}>In Transit Delay</span>
                                    <span> compares actual transit time to a standard transit time governed by Hours of Service (HOS) rules.  The calculation first determines the difference between actual order execution hours and the standard, expected execution hours. Next the difference is divided by the actual order execution hours. The quotient produces an In Transit ratio where zero is preferred.</span>
                                </Typography>
                            }
                        >
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-52750
                valueGetter: (_value, row): number | null => {
                    return row.netwise_in_transit_delay_hours_duration !== null && row.freight_order_count ?
                        row.netwise_in_transit_delay_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
            },
        ];
    }, []);

    const handlePaginationModelChange = (model: GridPaginationModel): void => {
        // TODO: TCSC-52750
        // dispatch(updateShipperHealthLanesApiParams({
        //     ...shipperHealthLanesApiParams,
        //     // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
        //     pageNumber: model.page + 1,
        //     pageSize: model.pageSize,
        // }));

        setPage(model.page);
        setPageSize(model.pageSize);
    };

    // TODO: TCSC-52750
    // const handleSortModelChange = (newSortModel: GridSortModel): void => {
    //     dispatch(updateShipperHealthLanesApiParams({
    //         ...shipperHealthLanesApiParams,
    //         sortBy: newSortModel,
    //     }));
    // };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    const healthFilters = formatHealthFilters();

    return (
        <StyledDataGridPremium
            classes={{
                root: classes.table,
                row: classes.tableRow,
            }}
            rows={shipperHealthLanes.lanes}
            columns={columns}
            density='compact'
            disableColumnFilter
            disableRowSelectionOnClick
            disableRowGrouping
            disableAggregation
            loading={shipperHealthLanesApiStatus === ApiStatus.Idle || shipperHealthLanesApiStatus === ApiStatus.Loading}
            getRowId={(row): string => { return getLaneId(row); }}
            // rowCount={shipperHealthLanes.lane_count}
            pagination
            // paginationMode='server'
            paginationModel={{
                // TODO: TCSC-52750
                // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                // page: shipperHealthLanesApiParams.pageNumber - 1,
                // pageSize: shipperHealthLanesApiParams.pageSize,

                page,
                pageSize,
            }}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            // sortingMode='server'
            // sortModel={shipperHealthLanesApiParams.sortBy}
            // onSortModelChange={handleSortModelChange}
            initialState={{
                // TODO: TCSC-52750
                sorting: {
                    sortModel: [
                        { field: 'freight_order_count', sort: 'desc' },
                        { field: 'average_load_hours_duration', sort: 'desc' },
                    ],
                },
            }}
            slotProps={{
                columnsManagement: {
                    searchPredicate: columnSearchPredicate,
                },
            }}
            onRowClick={(params: GridRowParams<Lane>): void => {
                navigate(buildLaneLink(params.row.pickup_service_area_id, params.row.delivery_service_area_id, {
                    ...filters,
                    earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
                    latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
                }));
            }}
        />
    );
};

export default ShipperHealthLanesTable;
